import Highcharts from 'highcharts';

/**
 * Default configuration for number formatting
 */
const DEFAULT_CONFIG = {
  scales: [
    { name: 'thousand', symbol: 'K', threshold: 1e3 },
    { name: 'million', symbol: 'MM', threshold: 1e6 },
    { name: 'billion', symbol: 'B', threshold: 1e9 },
    { name: 'trillion', symbol: 'T', threshold: 1e12 },
    { name: 'quadrillion', symbol: 'P', threshold: 1e15 },
    { name: 'quintillion', symbol: 'E', threshold: 1e18 },
  ],
  defaults: {
    decimals: 1,
    includeDollarSign: true,
    formatNegative: true,
    removeTrailingZeros: true,
  },
};

/**
 * Highcharts global number formatting settings
 */
export const configureHighchartsNumberFormat = () => {
  Highcharts.setOptions({
    lang: {
      numericSymbols: DEFAULT_CONFIG.scales.map((scale) => scale.symbol),
    },
  });
};

/**
 * Format a number with scale suffixes for Highcharts displays (e.g., "1.5MM", "$2.3B").
 *
 * This is specifically for Highcharts axis labels and tooltips where abbreviated
 * scale suffixes (K, MM, B, T) are appropriate. For general number formatting in
 * the UI (tables, counts, decimals), use `formatNumber()` from `utils/number.ts` instead.
 *
 * @param {number} value - The number to format
 * @param {number} [decimals=DEFAULT_CONFIG.defaults.decimals] - Number of decimal places
 * @param {boolean} [includeDollarSign=DEFAULT_CONFIG.defaults.includeDollarSign] - Whether to include $ symbol
 * @param {boolean} [formatNegative=DEFAULT_CONFIG.defaults.formatNegative] - Whether to format negative values with minus sign
 * @param {boolean} [removeTrailingZeros=DEFAULT_CONFIG.defaults.removeTrailingZeros] - Whether to remove trailing zeros
 * @return {string} Formatted number string (e.g., "$1.5MM", "2.3B", "500K")
 * @example
 * formatScaledNumber(1500000) // "$1.5MM"
 * formatScaledNumber(2300000000, 2, false) // "2.30B"
 */
export const formatScaledNumber = (
  value,
  decimals = DEFAULT_CONFIG.defaults.decimals,
  includeDollarSign = DEFAULT_CONFIG.defaults.includeDollarSign,
  formatNegative = DEFAULT_CONFIG.defaults.formatNegative,
  removeTrailingZeros = DEFAULT_CONFIG.defaults.removeTrailingZeros
) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }

  const sign = value < 0 ? '-' : '';
  const absValue = Math.abs(value);
  let formatted;

  const sortedScales = [...DEFAULT_CONFIG.scales].sort(
    (a, b) => b.threshold - a.threshold
  );

  const scale = sortedScales.find((scale) => absValue >= scale.threshold);

  if (scale) {
    // eslint-disable-next-line no-restricted-syntax -- internal formatting utility with scale suffixes
    formatted = (absValue / scale.threshold).toFixed(decimals) + scale.symbol;
  } else {
    // eslint-disable-next-line no-restricted-syntax -- internal formatting utility with scale suffixes
    formatted = absValue.toFixed(absValue === 0 ? 0 : decimals);
  }

  if (removeTrailingZeros) {
    formatted = formatted.replace(/\.0+([KMBTPE]?)$/, '$1');
  }

  const prefix = includeDollarSign ? '$' : '';
  return formatNegative ? sign + prefix + formatted : prefix + formatted;
};

/**
 * Highcharts axes formatter
 * @param {Object} options - Configuration options
 * @param {number} [options.decimals=DEFAULT_CONFIG.defaults.decimals] - Number of decimal places
 * @param {boolean} [options.includeDollarSign=DEFAULT_CONFIG.defaults.includeDollarSign] - Whether to include $ symbol
 * @return {Function} Formatter function for Highcharts
 */
export const createYAxisFormatter = (options = {}) => {
  const {
    decimals = DEFAULT_CONFIG.defaults.decimals,
    includeDollarSign = DEFAULT_CONFIG.defaults.includeDollarSign,
  } = options;

  return function () {
    // eslint-disable-next-line no-invalid-this
    return formatScaledNumber(this.value, decimals, includeDollarSign);
  };
};

export default {
  configureHighchartsNumberFormat,
  formatScaledNumber,
  createYAxisFormatter,
  DEFAULT_CONFIG,
};
